import React from 'react';

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  label?: string;
  name?: string;
}

/**
 * Switch component for toggling boolean values
 */
const Switch: React.FC<SwitchProps> = ({
  checked,
  onChange,
  disabled = false,
  className = '',
  size = 'md',
  label,
  name,
}) => {
  // Size variants - Updated with minimum 44px touch targets
  const sizes = {
    sm: {
      switch: 'w-11 h-6',
      dot: 'w-5 h-5',
      translate: 'translate-x-5',
      wrapper: 'min-h-[44px] p-2',
    },
    md: {
      switch: 'w-14 h-7',
      dot: 'w-6 h-6',
      translate: 'translate-x-7',
      wrapper: 'min-h-[44px] p-2',
    },
    lg: {
      switch: 'w-16 h-8',
      dot: 'w-7 h-7',
      translate: 'translate-x-8',
      wrapper: 'min-h-[48px] p-2',
    },
  };

  const sizeClasses = sizes[size];

  // Handle toggle
  const handleToggle = () => {
    if (!disabled) {
      onChange(!checked);
    }
  };

  // Handle keyboard accessibility
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
  };

  return (
    <div className={`inline-flex items-center ${sizeClasses.wrapper} ${className}`}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`relative inline-flex flex-shrink-0 items-center ${
          sizeClasses.switch
        } rounded-full transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
          checked
            ? 'bg-blue-600 dark:bg-blue-500'
            : 'bg-gray-200 dark:bg-gray-700'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        tabIndex={disabled ? -1 : 0}
        aria-label={label || 'Toggle'}
        name={name}
      >
        <span
          className={`${
            checked ? sizeClasses.translate : 'translate-x-0'
          } inline-block ${
            sizeClasses.dot
          } transform rounded-full bg-white shadow transition duration-200 ease-in-out`}
        />
      </button>
      {label && (
        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 select-none">
          {label}
        </span>
      )}
    </div>
  );
};

export default Switch;